import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Typography,
    Paper,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    Divider,
    Stack,
    IconButton,
    Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';

import {
    getProductById,
    getVideoGenerationsForProduct,
    getGenerationsForProduct,
} from '../services/api';

import type { Product, VideoGeneration } from '../types';

import { useVideoGeneration } from '../hooks/useVideoGeneration';

import VideoPreviewModal from '../components/product/VideoPreviewModal';

// Utility
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const VideoGenerationViewPage: React.FC = () => {
    const { productId: rawProductId, generationId: rawGenerationId } = useParams<{ productId: string; generationId?: string }>();
    const productId = rawProductId ? Number(rawProductId) : undefined;
    const generationIdParam = rawGenerationId ? Number(rawGenerationId) : undefined;
    const navigate = useNavigate();

    // Data
    const [loadedProduct, setLoadedProduct] = useState<Product | null>(null);

    // UI state
    const [numberOfVideos, setNumberOfVideos] = useState<number>(1);
    const [userPrompt, setUserPrompt] = useState<string>(''); // max 200 chars
    const [selectedReferenceImage, setSelectedReferenceImage] = useState<string | null>(null);
    const [showOnlyOriginals, setShowOnlyOriginals] = useState<boolean>(true);
    // Local loading state for generation button
    const [loading, setLoading] = useState(false);

    // Previously generated videos for this product
    const [previousGeneratedVideos, setPreviousGeneratedVideos] = useState<string[]>([]);
    const [previousGeneratedImages, setPreviousGeneratedImages] = useState<string[]>([]);

    // Status for selected generation
    const [status, setStatus] = useState<string>('');

    const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
    const [isVideoPreviewOpen, setIsVideoPreviewOpen] = useState(false);

    // --- Configuration for Video Generation ---
    const selectedPromptTemplateId = 11;
    const selectedPromptAiService = 'google_gemini-2.5-flash';
    const selectedVideoAiService = 'google_veo_3_fast';

    // On mount load product and history
    useEffect(() => {
        if (!productId) return;
        (async () => {
            try {
                const p = await getProductById(productId);
                setLoadedProduct(p);
                if (!selectedReferenceImage && p.images && p.images.length > 0) setSelectedReferenceImage(p.images[0]);
            } catch (err) {
                console.error('Failed to load product', err);
            }

            try {
                const gens: VideoGeneration[] = await getVideoGenerationsForProduct(productId);
                const urls: string[] = [];

                gens.forEach((g) => {
                    // Если открыт конкретный generationId — берём только видео этой генерации
                    if (generationIdParam) {
                        if (g.id === generationIdParam && g.video_result_details?.video_url) {
                            urls.push(g.video_result_details.video_url);
                        }
                    } else {
                        // Иначе добавляем все видео (например, для истории или будущего расширения)
                        if (g.video_result_details?.video_url) urls.push(g.video_result_details.video_url);
                    }
                });

                setPreviousGeneratedVideos(urls);
            } catch (err) {
                console.error('Failed to load previous video generations', err);
            }

            try {
                const gens = await getGenerationsForProduct(productId);
                const urls: string[] = [];
                gens.forEach((g) => {
                    g.image_requests?.forEach((req: any) => {
                        if (req.generated_image_url) urls.push(req.generated_image_url);
                    });
                });
                setPreviousGeneratedImages(urls);
            } catch (err) {
                console.error('Failed to load previous image generations', err);
            }

            // Additional: load status for specific generation if param is present
            if (generationIdParam && productId) {
              (async () => {
                try {
                  const gens: VideoGeneration[] = await getVideoGenerationsForProduct(productId);
                  const currentGen = gens.find(g => g.id === generationIdParam);
                  if (currentGen?.status) {
                    setStatus(currentGen.status);
                  } else if (currentGen?.video_result_details?.video_url) {
                    setStatus('completed');
                  } else {
                    setStatus('unknown');
                  }
                } catch (err) {
                  console.error('Failed to load video generation status', err);
                }
              })();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    const {
      startVideoGenerationFlow,
      statusText,
      generatedVideoUrl,
      generationStep,
      error,
    } = useVideoGeneration({
      selectedProductId: productId || '',
      selectedAIServiceCode: selectedPromptAiService,
      selectedVideoServiceCode: selectedVideoAiService,
      additionalInstructions: userPrompt,
      selectedPromptTemplateId: selectedPromptTemplateId,
      selectedReferenceImageUrl: selectedReferenceImage,
    });

    useEffect(() => {
      if (generationStep === 'done') {
        setLoading(false);
        setStatus('completed');
      } else if (generationStep === 'failed') {
        setLoading(false);
        setStatus('failed');
      } else if (generationStep === 'prompt_running') {
        setLoading(true);
        setStatus('Starting generation...');
      } else if (generationStep === 'video_running') {
        setLoading(true);
        setStatus('Generating video...');
      }
    }, [generationStep]);

    // Video status mapping and dynamic status for UI
    let videoStatus = '';
    if (status) {
      videoStatus = status;
    } else if (generationStep === 'done') {
      videoStatus = 'completed';
    } else if (generationStep === 'failed') {
      videoStatus = 'failed';
    } else if (generationStep === 'prompt_running') {
      videoStatus = 'Starting generation...';
    } else if (generationStep === 'video_running') {
      videoStatus = 'Generating video...';
    } else {
      videoStatus = 'unknown';
    }

    const productName = loadedProduct?.name ?? `#${productId}`;

    const handleBack = () => {
        if (!productId) return navigate('/dashboard');
        navigate(`/dashboard/catalog/${productId}`);
    };

    const referenceImages = showOnlyOriginals
        ? loadedProduct?.images ?? []
        : [...(loadedProduct?.images ?? []), ...previousGeneratedImages];

    // Формирование массива videoUrls по условиям задачи
    const videoUrls = generationIdParam
      ? previousGeneratedVideos // для просмотра существующей генерации
      : generatedVideoUrl
      ? [generatedVideoUrl] // для новой генерации, только новые видео
      : [];

    // Download all videos function
    const downloadAllVideos = async () => {
        const urls = [generatedVideoUrl, ...previousGeneratedVideos].filter(Boolean) as string[];
        for (let i = 0; i < urls.length; i++) {
            const url = urls[i];
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error('Failed to fetch video');
                const blob = await response.blob();
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = `video_${i + 1}.mp4`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
            } catch (err) {
                console.error('Download failed', err);
            }
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <IconButton onClick={handleBack} size="large"><ArrowBackIcon /></IconButton>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Video Generation for{' '}
                    <Box component="span" sx={{ color: 'orange', fontStyle: 'italic' }}>
                        “{productName}”
                    </Box>
                </Typography>
            </Box>

            <Paper sx={{ p: 3 }} elevation={1}>
                {/* Top controls */}
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', mb: 2 }}>
                    <Button
                        variant="outlined"
                        sx={{ minWidth: 150, minHeight: 56, alignSelf: 'stretch', flexShrink: 0 }}
                    >
                        Select Marketplace
                    </Button>
                    <TextField
                        label="Number of Videos"
                        type="number"
                        value={numberOfVideos}
                        onChange={(e) => setNumberOfVideos(Math.max(1, Math.min(10, Number(e.target.value || 1))))}
                        sx={{ minWidth: 150, minHeight: 56, alignSelf: 'stretch', flexShrink: 0 }}
                        inputProps={{ min: 1, max: 10 }}
                        size="medium"
                    />
                    <TextField
                        label="User Prompt (optional)"
                        value={userPrompt}
                        onChange={(e) => setUserPrompt(e.target.value.slice(0, 200))}
                        placeholder="Short tweak or note (max 200 chars)"
                        sx={{ flexGrow: 1, minWidth: 150, minHeight: 56, alignSelf: 'stretch' }}
                        inputProps={{ maxLength: 200 }}
                        size="medium"
                    />
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (generationIdParam) return;
                            try {
                                setLoading(true);
                                setStatus('Starting generation...');
                                await startVideoGenerationFlow();
                            } catch (err) {
                                setStatus('Error generating video');
                            }
                        }}
                        disabled={
                            loading ||
                            generationStep === 'prompt_running' ||
                            generationStep === 'video_running' ||
                            Boolean(generationIdParam)
                        }
                        sx={{ minWidth: 140, minHeight: 56, alignSelf: 'stretch', flexShrink: 0 }}
                    >
                        {loading ||
                        generationStep === 'prompt_running' ||
                        generationStep === 'video_running' ? (
                            <CircularProgress size={20} />
                        ) : generationIdParam ? (
                            'View Only'
                        ) : (
                            'Generate'
                        )}
                    </Button>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Reference image carousel and toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Reference Image</Typography>
                    <FormControlLabel
                        control={<Switch checked={showOnlyOriginals} onChange={() => setShowOnlyOriginals((s) => !s)} size="small" />}
                        label="Only Originals"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                    />
                </Box>

                <Box sx={{ display: 'flex', overflowX: 'auto', gap: 1, whiteSpace: 'nowrap', pb: 1, mb: 2 }}>
                    {referenceImages.map((img, i) => (
                        <Box
                            key={`ref-${i}`}
                            sx={{ width: 80, height: 80, flex: '0 0 auto', borderRadius: 1, overflow: 'hidden', cursor: 'pointer', border: selectedReferenceImage === img ? '2px solid orange' : '1px solid #ccc' }}
                            onClick={() => setSelectedReferenceImage(img)}
                        >
                            <img src={img} alt={`ref-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                    ))}

                    {/* original images */}
                    {/* {loadedProduct?.images?.map((img, i) => (
                        <Box
                            key={`orig-${i}`}
                            sx={{ width: 80, height: 80, flex: '0 0 auto', borderRadius: 1, overflow: 'hidden', cursor: 'pointer', border: selectedReferenceImage === img ? '2px solid orange' : '1px solid #ccc' }}
                            onClick={() => setSelectedReferenceImage(img)}
                        >
                            <img src={img} alt={`orig-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </Box>
                    ))} */}

                    {/* previous generated videos (show as thumbnail placeholders) */}
                    {/* {!showOnlyOriginals && previousGeneratedVideos.map((url, i) => (
                        <Box
                            key={`prev-${i}`}
                            sx={{ width: 80, height: 80, flex: '0 0 auto', borderRadius: 1, overflow: 'hidden', cursor: 'pointer', border: selectedReferenceImage === url ? '2px solid orange' : '1px solid #ccc', position: 'relative' }}
                            onClick={() => setSelectedReferenceImage(url)}
                        >
                            <video src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
                        </Box>
                    ))} */}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Status area */}
                {!generationIdParam && !loading && (generationStep === 'idle' || generationStep === 'unknown') ? null : (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color={
                        videoStatus === 'completed'
                          ? 'success.main'
                          : videoStatus === 'failed' || videoStatus === 'Error generating video'
                          ? 'error.main'
                          : 'text.secondary'
                      }
                    >
                      Status: {videoStatus}
                    </Typography>
                  </Box>
                )}

                {/* Result / preview */}
                <Grid container spacing={2}>
                  {videoUrls.map((url, i) => (
                    <Grid item key={i} xs={12} sm={6} md={4} lg={3}>
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 1,
                          overflow: 'hidden',
                          boxShadow: 1,
                          width: '100%',
                          aspectRatio: '16/9',
                        }}
                        onClick={() => {
                          setPreviewVideoUrl(url);
                          setIsVideoPreviewOpen(true);
                        }}
                      >
                        <video
                          src={url}
                          width="100%"
                          height="100%"
                          controls
                          style={{ objectFit: 'cover' }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(255,255,255,0.7)',
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const response = await fetch(url);
                              if (!response.ok) throw new Error('Failed to fetch video');
                              const blob = await response.blob();
                              const blobUrl = window.URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = blobUrl;
                              a.download = `video_${i + 1}.mp4`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(blobUrl);
                            } catch (err) {
                              console.error('Download failed', err);
                            }
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {/* Download All button */}
                {videoUrls.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={downloadAllVideos}
                    >
                      Download All
                    </Button>
                  </Box>
                )}

            </Paper>
            <VideoPreviewModal
                videoUrl={previewVideoUrl || ''}
                open={isVideoPreviewOpen}
                onClose={() => setIsVideoPreviewOpen(false)}
            />
        </Box>
    );
};

export default VideoGenerationViewPage;
